import dayjs from "dayjs";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT, SUBSCRIPTION_FORM_KEYS } from "utils/constants";
import {
  clearPpeDetails,
  isPpeEndDateBeforeStart,
  shouldShowPpeDetails,
} from "../ppeDeclaration";

const Ppe = ({ bindField, initialValues, setPercent }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const ppeCopy = t("EFFECTIVE_DATE.FORM.PPE", { returnObjects: true });
  const isPepPath = "project.contact.politicallyExposedPerson";
  const functionPath = "project.contact.fields.ppeFunction";
  const startDatePath = "project.contact.fields.ppeStartDate";
  const endDatePath = "project.contact.fields.ppeEndDate";
  const isPep = get(initialValues, isPepPath);
  const showDetails = shouldShowPpeDetails(isPep);

  const isPepField = bindField(isPepPath, {
    label: ppeCopy.DECLARATION,
    required: true,
    onChange: (value) => {
      if (value !== true) {
        const cleared = clearPpeDetails();
        set(initialValues, functionPath, cleared.ppeFunction);
        set(initialValues, startDatePath, cleared.ppeStartDate);
        set(initialValues, endDatePath, cleared.ppeEndDate);
      }
      setTimeout(() => setPercent(initialValues), 50);
    },
  });

  const functionField = bindField(functionPath, {
    label: ppeCopy.FUNCTION,
    required: true,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const startDateField = bindField(startDatePath, {
    label: ppeCopy.START_DATE,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const endDateField = bindField(endDatePath, {
    label: ppeCopy.END_DATE,
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PPE}-section`}
    >
      <Form type="vertical" title={ppeCopy.TITLE}>
        <div className="grid--1">
          <FormField
            path={isPepField.path}
            label={isPepField.label}
            required={isPepField.required}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                values={[
                  { label: ppeCopy.YES, value: true },
                  { label: ppeCopy.NO, value: false },
                ]}
                {...field}
                value={isPep}
                label={ppeCopy.QUESTION}
                onChange={isPepField.onChange}
              />
            )}
          </FormField>
          {showDetails && (
            <>
              <FormField
                path={functionField.path}
                label={functionField.label}
                required={functionField.required}
              >
                {(field) => (
                  <Input
                    {...field}
                    autoComplete="off"
                    label={ppeCopy.FUNCTION}
                    defaultValue={functionField.defaultValue}
                    onChange={functionField.onChange}
                    onBlur={functionField.onBlur}
                  />
                )}
              </FormField>
              <FormField
                path={startDateField.path}
                label={startDateField.label}
                required={startDateField.required}
              >
                {(field) => (
                  <Input
                    type="date"
                    label={ppeCopy.START_DATE}
                    format={DATE_FORMAT}
                    disabledDate={(current) =>
                      isPpeEndDateBeforeStart(
                        current,
                        get(initialValues, endDatePath),
                      )
                    }
                    defaultValue={
                      get(initialValues, startDatePath) &&
                      dayjs(get(initialValues, startDatePath))
                    }
                    {...field}
                    onChange={startDateField.onChange}
                    onBlur={startDateField.onBlur}
                  />
                )}
              </FormField>
              <FormField path={endDateField.path} label={endDateField.label}>
                {(field) => (
                  <Input
                    type="date"
                    label={ppeCopy.END_DATE}
                    format={DATE_FORMAT}
                    optional
                    disabledDate={(current) =>
                      isPpeEndDateBeforeStart(
                        get(initialValues, startDatePath),
                        current,
                      )
                    }
                    defaultValue={
                      get(initialValues, endDatePath) &&
                      dayjs(get(initialValues, endDatePath))
                    }
                    {...field}
                    onChange={endDateField.onChange}
                    onBlur={endDateField.onBlur}
                  />
                )}
              </FormField>
            </>
          )}
        </div>
      </Form>
    </div>
  );
};

export default Ppe;
